/**
 * Bundle sync — keeps 3 surfaces aligned when user toggles addon checkboxes:
 *
 *  1. Savings footer (inside .product-addon-bundle) — shows total savings
 *     when sum(strike - price) > 0 across checked addons. Hidden otherwise.
 *  2. Total line (above CTA, [data-bundle-total]) — "Total cu accesorii ·
 *     X Lei" + "iPhone + N accesorii". Hidden when 0 addons checked.
 *  3. Sticky bar — price col reflects total, meta line shows "+N accesorii".
 *
 *  Accordion toggles for folie / husă groups.
 *  Ship banner state — active when folie OR husă selected.
 *  Count badges on accordion triggers.
 *
 *  Warranty toggle: [data-warranty-toggle] button flips "is-active" on its
 *  pill child and is included in the cart on next submit when active.
 *
 *  All prices are in cents (Shopify convention). Base price comes from
 *  [data-base-price] on each surface root, addon prices/strikes from
 *  [data-addon-price] / [data-addon-strike] on each checkbox.
 */

(() => {
  const bundle = document.querySelector('[data-product-bundle]');
  if (!bundle) return;

  const basePrice = parseInt(bundle.dataset.basePrice, 10) || 0;

  // Collect all interactive inputs: checkboxes AND folie radios
  const checkboxes = bundle.querySelectorAll('.product-addon-bundle__cb');

  // Warranty state — declared here so recompute() closure can read it
  let warrantyActive = false;
  const warrantyBtn  = bundle.querySelector('[data-warranty-toggle]');

  // Savings footer
  const savingsEl = bundle.querySelector('[data-bundle-savings]');
  const savingsAmountEl = bundle.querySelector('[data-bundle-savings-amount]');
  const savingsDetailEl = bundle.querySelector('[data-bundle-savings-detail]');

  // Total line (in CTA block)
  const totalEl = document.querySelector('[data-bundle-total]');
  const totalPriceEl = document.querySelector('[data-bundle-total-price]');
  const totalSubEl = document.querySelector('[data-bundle-total-sub]');

  // Sticky bar
  const stickyPriceEl = document.querySelector('[data-bundle-sticky-price]');
  const stickyMetaEl = document.querySelector('[data-bundle-sticky-meta]');
  const stickyMetaTextEl = document.querySelector('[data-bundle-sticky-meta-text]');
  const stickyCompareEl = document.querySelector('[data-bundle-sticky-compare]');
  const baseCompare = stickyCompareEl
    ? parseInt(stickyCompareEl.dataset.baseCompare, 10) || 0
    : 0;

  // ===== Money formatting =====
  // Format cents → "5.799 Lei" (Romanian locale, dot thousands separator).
  // Whole-Lei display matches the existing `money_without_trailing_zeros`
  // | replace ',00' patterns used in the static Liquid.
  const _leiFormatter = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 });
  const fmt = (cents) => _leiFormatter.format(Math.round(cents / 100)) + ' Lei';

  const accLabel = (n) => (n === 1 ? '1 accesoriu' : n + ' accesorii');

  // ===== Count badge update =====
  function updateAccBadges() {
    // Folie: radio — 0 or 1
    const folieRadio = bundle.querySelector('input[type="radio"][name="folie"]:checked');
    const folieCountEl = bundle.querySelector('[data-acc-count="folie"]');
    if (folieCountEl) {
      if (folieRadio) {
        const price = parseInt(folieRadio.dataset.addonPrice, 10) || 0;
        folieCountEl.innerHTML = '1 aleasă · ' + fmt(price) +
          ' <span class="product-addon-bundle__acc-clear" data-acc-clear="folie" role="button" tabindex="0" aria-label="Elimină folie">×</span>';
        folieCountEl.hidden = false;
      } else {
        folieCountEl.textContent = '';
        folieCountEl.hidden = true;
      }
    }

    // Husă: checkboxes — 0 to N
    const husaChecked = bundle.querySelectorAll('input[type="checkbox"][data-addon-group="husa"]:checked');
    const husaCountEl = bundle.querySelector('[data-acc-count="husa"]');
    if (husaCountEl) {
      if (husaChecked.length > 0) {
        let husaTotal = 0;
        husaChecked.forEach((cb) => { husaTotal += parseInt(cb.dataset.addonPrice, 10) || 0; });
        const husaWord = husaChecked.length === 1 ? '1 aleasă' : husaChecked.length + ' alese';
        husaCountEl.textContent = husaWord + ' · ' + fmt(husaTotal);
        husaCountEl.hidden = false;
      } else {
        husaCountEl.hidden = true;
      }
    }
  }

  // ===== Recompute & sync all surfaces =====
  function recompute() {
    let total = basePrice;
    let totalCompareDelta = 0;
    let count = 0;
    let totalSavings = 0;
    const breakdown = [];
    const addonLabels = [];

    // Warranty (Group D) listed first — highest user intent signal
    if (warrantyActive && warrantyBtn) {
      const warrantyPrice = parseInt(warrantyBtn.dataset.addonPrice, 10) || 0;
      const warrantyLabel = warrantyBtn.dataset.addonLabel || 'Garanție extinsă';
      total += warrantyPrice;
      totalCompareDelta += warrantyPrice;
      count += 1;
      addonLabels.push(warrantyLabel);
      breakdown.push(fmt(warrantyPrice) + ' · ' + warrantyLabel.toLowerCase());
    }

    // Battery upgrade — set on .battery-cards by battery-selector.js
    const batteryCards = document.querySelector('.battery-cards');
    if (batteryCards?.dataset.batteryPrice) {
      const batteryPrice = parseInt(batteryCards.dataset.batteryPrice, 10) || 0;
      const batteryLabel = batteryCards.dataset.batteryLabel || 'Baterie';
      total += batteryPrice;
      count += 1;
      totalCompareDelta += batteryPrice;
      addonLabels.push(batteryLabel);
    }

    checkboxes.forEach((cb) => {
      if (!cb.checked) return;
      if (cb.dataset.addonLocked === 'true') return; // locked default addons are free, skip price/count
      const price = parseInt(cb.dataset.addonPrice, 10) || 0;
      const strike = parseInt(cb.dataset.addonStrike, 10) || 0;
      const label = cb.dataset.addonLabel || '';
      total += price;
      // Compare side gets the higher of strike vs price — i.e. the
      // pre-savings reference value for this addon. No-strike addons
      // contribute their price (zero implied savings on that item).
      totalCompareDelta += strike > price ? strike : price;
      count += 1;
      if (label) addonLabels.push(label);
      if (strike > 0 && strike > price) {
        const saving = strike - price;
        totalSavings += saving;
        breakdown.push(fmt(saving) + ' · ' + label.toLowerCase());
      }
    });

    // Savings footer
    if (savingsEl) {
      if (totalSavings > 0) {
        if (savingsAmountEl) savingsAmountEl.textContent = fmt(totalSavings);
        if (savingsDetailEl) savingsDetailEl.textContent = breakdown.join(' + ');
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    // Total line above CTA — name what's added, not abstract count
    if (totalEl) {
      if (count > 0) {
        if (totalPriceEl) totalPriceEl.textContent = fmt(total);
        if (totalSubEl) {
          let subLine;
          if (addonLabels.length === 1) {
            subLine = 'iPhone + ' + addonLabels[0];
          } else if (addonLabels.length === 2) {
            subLine = 'iPhone + ' + addonLabels[0] + ' + ' + addonLabels[1];
          } else if (addonLabels.length > 2) {
            const rest = addonLabels.length - 1;
            subLine = 'iPhone + ' + addonLabels[0] + ' + ' + rest + ' alte';
          } else {
            subLine = 'iPhone + ' + accLabel(count);
          }
          totalSubEl.textContent = subLine;
        }
        totalEl.hidden = false;
      } else {
        totalEl.hidden = true;
      }
    }

    // Sticky bar price col + meta
    if (stickyPriceEl) {
      stickyPriceEl.textContent = fmt(count > 0 ? total : basePrice);
    }
    if (stickyCompareEl && baseCompare > 0) {
      stickyCompareEl.textContent = fmt(baseCompare + totalCompareDelta);
    }
    if (stickyMetaEl) {
      if (count > 0) {
        if (stickyMetaTextEl) stickyMetaTextEl.textContent = '+ ' + accLabel(count);
        stickyMetaEl.hidden = false;
      } else {
        stickyMetaEl.hidden = true;
      }
    }

    updateAccBadges();

    // Broadcast the gross total (bani) so the LeanPay calculator can re-base its
    // installment on the configured cart (base variant + checked addons). count
    // 0 → total === basePrice, which resets LeanPay to the bare product.
    document.dispatchEvent(new CustomEvent('bundle:change', {
      detail: { total, count },
    }));
  }

  checkboxes.forEach((cb) => cb.addEventListener('change', recompute));
  document.addEventListener('battery:change', recompute);
  recompute();

  // ===== Pill "×" clear — uncheck folie radio without opening accordion =====
  // The pill lives inside the accordion trigger button, so we stopPropagation
  // to avoid the trigger's click toggling the panel. The folie radio's
  // deselect behaviour itself is handled by <cos-deselectable-radios>.
  bundle.addEventListener('click', (e) => {
    const clearBtn = e.target.closest('[data-acc-clear="folie"]');
    if (!clearBtn) return;
    e.stopPropagation();
    e.preventDefault();
    const radio = bundle.querySelector('input[type="radio"][name="folie"]:checked');
    if (!radio) return;
    radio.checked = false;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  bundle.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const clearBtn = e.target.closest('[data-acc-clear="folie"]');
    if (!clearBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const radio = bundle.querySelector('input[type="radio"][name="folie"]:checked');
    if (!radio) return;
    radio.checked = false;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ===== Warranty toggle (Group D — single featured row) =====
  // Manages active state + appends warranty variant to cart on form submit.
  // warrantyActive + warrantyBtn declared above recompute() for closure access.
  if (warrantyBtn) {
    const warrantyPill = warrantyBtn.querySelector('[data-warranty-state]');
    const addCta    = warrantyBtn.getAttribute('data-warranty-add-cta') || '+ Adaugă';
    const removeCta = warrantyBtn.getAttribute('data-warranty-remove-cta') || 'Scoate';

    warrantyBtn.addEventListener('click', () => {
      warrantyActive = !warrantyActive;
      warrantyBtn.classList.toggle('is-active', warrantyActive);
      if (warrantyPill) {
        warrantyPill.dataset.warrantyState = warrantyActive ? 'on' : 'off';
        warrantyPill.classList.toggle('is-active', warrantyActive);
        warrantyPill.textContent = warrantyActive ? removeCta : addCta;
      }
      recompute();
    });

    // Cart-add injection lives in buy-button.js _getAddonItems (queries
    // [data-warranty-toggle].is-active). A form.submit listener here is
    // dead code — buy-button.js preventDefaults the click and POSTs JSON
    // to /cart/add.js without invoking form.submit().
  }

  // ===== Accordion toggle (folie + husă groups) =====
  // Warranty trigger does NOT have aria-controls, so querySelectorAll only
  // picks up folie/husă triggers (which do have aria-controls).
  const accTriggers = bundle.querySelectorAll('.product-addon-bundle__acc-trigger[aria-controls]');
  accTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      panel.hidden = isExpanded;
    });
  });

  // ===== "?" tooltip on charger pill (legacy — kept for compat) =====
  const infoButtons = bundle.querySelectorAll('.product-addon-bundle__info-toggle');
  infoButtons.forEach((btn) => {
    const tipId = btn.getAttribute('aria-controls');
    const tip = tipId ? document.getElementById(tipId) : null;
    if (!tip) return;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = tip.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    tip.addEventListener('click', (e) => e.stopPropagation());
  });

  document.addEventListener('click', (e) => {
    infoButtons.forEach((btn) => {
      const tipId = btn.getAttribute('aria-controls');
      const tip = tipId ? document.getElementById(tipId) : null;
      if (!tip || !tip.classList.contains('is-open')) return;
      if (e.target === btn || tip.contains(e.target)) return;
      tip.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    infoButtons.forEach((btn) => {
      const tipId = btn.getAttribute('aria-controls');
      const tip = tipId ? document.getElementById(tipId) : null;
      if (!tip) return;
      tip.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });
})();
